import { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';
import {
  getStoredConsent,
  hasConsentDecision,
  isDoNotTrackEnabled,
  setConsentAccepted,
  setConsentDeclined,
} from '../utils/consent';

function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Respect Do Not Track: don't show the banner. Tracking is blocked by the
    // consent module regardless, so there is nothing for the user to decide.
    if (isDoNotTrackEnabled()) return;
    // Only prompt if the user hasn't already made an explicit choice or dismissed.
    if (!hasConsentDecision() && getStoredConsent() !== 'seen') {
      setIsVisible(true);
    }
  }, []);

  // Accessibility: Allow ESC key to dismiss cookie banner
  useEffect(() => {
    if (!isVisible) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isVisible]);

  const handleAccept = () => {
    // Persists consent + notifies subscribers (main.jsx) to start tracking.
    setConsentAccepted();
    setIsVisible(false);

    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'analytics_storage': 'granted'
      });
    }
  };

  const handleDecline = () => {
    setConsentDeclined();
    setIsVisible(false);
  };

  const handleClose = () => {
    // Dismissed without an explicit choice: not consent. Banner stays hidden.
    localStorage.setItem('cookie-consent', 'seen');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900 text-white p-4 shadow-2xl"
      role="dialog"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-description"
    >
      <div className="container-custom mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          {/* Icon and Message */}
          <div className="flex items-start space-x-3 flex-1">
            <Cookie className="w-6 h-6 text-primary-400 flex-shrink-0 mt-1" aria-hidden="true" />
            <div>
              <h3 id="cookie-title" className="font-semibold mb-1">Cookie Consent</h3>
              <p id="cookie-description" className="text-gray-300 text-sm">
                We use cookies to enhance your experience, analyze traffic, and serve personalized ads.
                By continuing to visit this site you agree to our use of cookies.
                Press Escape to close this banner.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
            <button
              onClick={handleAccept}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium transition-colors min-h-[44px] flex items-center justify-center"
            >
              Accept All
            </button>
            <button
              onClick={handleDecline}
              className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors min-h-[44px] flex items-center justify-center"
            >
              Decline
            </button>
            <button
              onClick={handleClose}
              className="p-3 text-gray-400 hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Close cookie banner (press Escape)"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Links */}
        <nav className="mt-4 pt-4 border-t border-gray-700 flex flex-wrap gap-4 text-xs" aria-label="Legal links">
          <a href="/privacy-policy" className="hover:text-white transition-colors underline">
            Privacy Policy
          </a>
          <a href="/terms-conditions" className="hover:text-white transition-colors underline">
            Terms & Conditions
          </a>
          <a href="/cookie-policy" className="hover:text-white transition-colors underline">
            Cookie Policy
          </a>
        </nav>
      </div>
    </div>
  );
}

export default CookieConsent;
